import { create } from 'zustand';
import { isProductHiddenFromUI } from '../lib/productVisibility';

// في التطوير: استخدم نفس المنفذ (البروكسي في Vite يمرر /api إلى الباكند) لتجنب CORS
const BACKEND_URL = import.meta.env.DEV
  ? ''
  : (import.meta.env.VITE_BACKEND_URL || 'https://app.teen-sa.com').replace(/\/$/, '');

/** رسالة آمنة للعرض في الواجهة فقط — لا نعرض أبداً رابط الباكند أو نصوص تحتوي روابط */
function sanitizeErrorMessage(raw: string | undefined): string {
  if (!raw || typeof raw !== 'string') return 'فشل الاتصال بالخادم';
  // رسائلنا المعروفة آمنة للعرض
  if (/^فشل جلب المنتجات \(\d+\)$/.test(raw.trim())) return raw.trim();
  if (raw.trim() === 'فشل الاتصال بالخادم') return raw.trim();
  // تجنب عرض أي نص يحتوي على روابط (http/https) أو مسارات
  if (/https?:\/\//i.test(raw) || /\/api\//i.test(raw)) return 'فشل الاتصال بالخادم';
  // رسائل عامة من المتصفح مثل "Failed to fetch" نعرضها كـ فشل الاتصال
  if (/failed to fetch|network error|load failed/i.test(raw)) return 'فشل الاتصال بالخادم';
  return raw.length > 120 ? 'فشل الاتصال بالخادم' : raw;
}

export interface Product {
  id: number;
  title: string;
  description: string;
  price: number;
  originalPrice?: number;
  image: string;
  category?: string;
  soldOut?: boolean;
  new?: boolean;
  preOrder?: boolean;
  /** مدة التحضير بالدقائق (0 = غير محدد) */
  prepTimeMinutes?: number;
  /** لا يُعرض في المتجر (يتوافق مع الباكند) */
  hidden?: boolean;
}

interface ProductState {
  products: Product[];
  isLoading: boolean;
  error: string | null;
  hasFetched: boolean;
  fetchProducts: (forceRefresh?: boolean) => Promise<void>;
}

const MAX_RETRIES = 3;
// Exponential backoff: 1s → 3s → 7s (يغطي cold start لـ Railway)
const RETRY_DELAYS_MS = [1000, 3000, 7000];

/** كشف البوتات — لتجنب إرسال ضجيج Sentry من الزواحف */
function isBot(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /bot|crawl|spider|adsbotcrawler|snapchatads|facebookexternalhit|AdsBot/i.test(
    navigator.userAgent
  );
}

// متغير خارجي لمنع الطلبات المتزامنة
let isFetchingProducts = false;

export const useProductStore = create<ProductState>((set, get) => ({
  products: [],
  isLoading: false,
  error: null,
  hasFetched: false,
  fetchProducts: async (forceRefresh = false) => {
    const state = get();

    // منع الطلبات المكررة - إذا تم التحميل بنجاح أو جاري التحميل (ما لم يكن forceRefresh)
    if (!forceRefresh && (state.hasFetched || state.isLoading || isFetchingProducts)) {
      return;
    }

    isFetchingProducts = true;
    set({ isLoading: true, error: null });

    let lastError: any = null;

    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      try {
        const timestamp = new Date().getTime();
        const response = await fetch(`${BACKEND_URL}/api/products?t=${timestamp}`, {
          cache: 'no-cache',
          headers: { 'Cache-Control': 'no-cache' },
        });
        if (!response.ok) {
          throw new Error(`فشل جلب المنتجات (${response.status})`);
        }
        const json = await response.json();
        const raw = Array.isArray(json) ? json : [];
        const list = raw.filter((p: Product) => !isProductHiddenFromUI(p));
        set({ products: list, isLoading: false, hasFetched: true, error: null });
        isFetchingProducts = false;
        return;
      } catch (err: any) {
        lastError = err;
        if (attempt < MAX_RETRIES) {
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAYS_MS[attempt - 1]));
        }
      }
    }

    // فشلت جميع المحاولات — لا نُبلّغ Sentry إذا كان المصدر بوت
    const rawMessage = lastError?.message || 'فشل الاتصال بالخادم';
    const displayMessage = sanitizeErrorMessage(rawMessage);
    if (!isBot()) {
      try {
        const Sentry = await import('@sentry/react');
        Sentry.captureException(lastError, {
          tags: { feature: 'products_fetch' },
          extra: { message: rawMessage, url: `${BACKEND_URL}/api/products`, attempts: MAX_RETRIES },
        });
      } catch (_) {}
    }

    set({
      products: [],
      error: displayMessage,
      isLoading: false,
      hasFetched: false, // يبقى false ليُعيد المحاولة عند العودة للصفحة
    });
    isFetchingProducts = false;
  },
}));

